/* global Sys, Core, Services, Language, Game */
Sys.ns("Core");

/**
 * The Custom Messages controller.
 *
 * @class Core.CustomMessagesController
 * @extends Core.Controller
 */
Core.CustomMessagesController = {

    DIALOG_DELAY: 3000,

    ERRORS: {
        DISPLAY_IMMEDIATELY_CLOSE: 990,
        DISPLAY_IMMEDIATELY_CONTINUE: 991,
        DISPLAY_ON_IDLE_CLOSE: 992,
        DISPLAY_ON_IDLE_CONTINUE: 993
    },

    /**
     * @inheritdoc
     */
    constructor: function() {
        Core.CustomMessagesController.superclass.constructor.apply(this, arguments);
    },

    /**
     * @inheritdoc
     */
    setupEvents: function() {
        this.on({
            "request:customMessages.showDialog": this.onShowDialogRequest,
            "request:customMessages.removeDialog": this.onRemoveDialogRequest,
            "notify:serverManager.serverResponseReceived": this.onServerResponseReceived,
            "notify:serverManager.serverErrorReceived": this.onServerErrorReceived,
            "notify:stateHandler.enteringSetupGameState": this.onEnteringSetupGameState,
            "notify:stateHandler.enteringIdleState": this.onEnteringIdleState,
            "notify:stateHandler.leavingIdleState": this.onLeavingIdleState,
            "notify:stateHandler.enteringShowingCustomMessagesState": this.onEnteringShowingCustomMessagesState,
            "notify:stateHandler.leavingBeforeLoaderCloseState": this.onLoaderClose,
            "notify:stateHandler.enteringDelayBeforeShowingMessagesState": this.onEnteringDelayBeforeShowingMessagesState,
            "notify:stateHandler.leavingDelayBeforeShowingMessagesState": this.onLeavingDelayBeforeShowingMessagesState,
            "notify:dialogWindow.dialogShown": this.onDialogShown
        });
    },

    /**
     * Store that the loader is closed and that we can show dialogs in game.
     *
     * @private
     * @returns {void}
     */
    onLoaderClose: function() {
        this.model.storeData("showingLoader", false);
        this.model.storeData("shouldShowDialogs", true);
    },

    /**
     * On show dialog request.
     *
     * @private
     * @param {Object} dialogConfig The dialog config.
     * @param {string} dialogConfig.id The dialog id.
     * @param {string} dialogConfig.title The dialog title.
     * @param {string} dialogConfig.text The dialog text.
     * @param {Object[]} dialogConfig.buttons Array of button configurations on the form { text : {String}, action (optional) : {Function}, scope (defaults to window) : {Object} }.
     * @returns {void}
     */
    onShowDialogRequest: function(dialogConfig) {
        var preparedConfig;

        try {
            preparedConfig = this.prepareDialogConfig(dialogConfig);
        }
        catch (e) {
            this.showTechnicalError();
            this.model.storeData("shouldShowDialogs", false);
            return;
        }

        delete preparedConfig.immediate;

        if (this.model.readData("isIdle")) {
            if (this.model.hasDialogsToWaitFor()) {
                this.model.addDialogToWaitFor(preparedConfig.id);
            }

            Services.dialog.show(preparedConfig);
        }
        else {
            this.model.addDialog(preparedConfig);
        }
    },

    /**
     * On remove dialog request.
     *
     * @private
     * @param {string} id The dialog id.
     * @returns {void}
     */
    onRemoveDialogRequest: function(id) {
        this.model.removeDialogToWaitFor(id);
        this.model.removeDialog(id);
        Services.dialog.remove(id);
    },

    /**
     * Handles messages from server.
     *
     * @private
     * @param {XMLHttpRequest} serverRequest Server request.
     * @returns {void}
     */
    onServerResponseReceived: function(serverRequest) {
        var parameters = Sys.utils.queryStringToObject(serverRequest.responseText, true),
            numParametersKeys = Object.keys(parameters).length;

        if (Sys.isDefined(parameters.messages)) {
            this.handleMessagesFromServerRequest(parameters.messages, numParametersKeys === 1);
        }
    },

    /**
     * Parses the server response.
     *
     * @private
     * @param {XMLHttpRequest} serverRequest Server request.
     * @returns {void}
     */
    onServerErrorReceived: function(serverRequest) {
        var parameters = Sys.utils.queryStringToObject(serverRequest.responseText, true);

        if (Sys.isDefined(parameters.messages)) {
            this.handleMessagesFromServerRequest(parameters.messages, true);
        }
    },

    /**
     * Handles messages from server request.
     *
     * @private
     * @param {string} messageString The URI decoded message string from the server response.
     * @param {boolean} [wasInsufficientGameResponse=false] Whether or not the server response contained enough information for the game round to proceed.
     * @returns {void}
     */
    handleMessagesFromServerRequest: function(messageString, wasInsufficientGameResponse) {
        var messages,
            lastMessage,
            configMessage,
            preparedConfig;

        try {
            messages = JSON.parse(messageString);

            if (Sys.isArray(messages)) {
                lastMessage = messages.pop();

                if (Sys.isDefined(lastMessage)) {
                    configMessage = this.createConfigFromMessage(lastMessage);
                    preparedConfig = this.prepareDialogConfig(configMessage);
                    this.model.addDialog(preparedConfig);
                }

                if (wasInsufficientGameResponse) {
                    this.showImmediateMessages();
                }
            }
        }
        catch (e) {
            this.showTechnicalError();
            this.model.storeData("shouldShowDialogs", false);
        }
    },

    /**
     * Unpauses the loader if there are now errors showing.
     *
     * @private
     * @returns {void}
     */
    onEnteringSetupGameState: function() {
        if (!this.model.readData("showingError")) {
            this.fireEvent("notify:customMessages.ready");
        }
    },

    /**
     * On entering idle state.
     *
     * @private
     * @returns {void}
     */
    onEnteringIdleState: function() {
        this.model.storeData("isIdle", true);

        if (!this.model.readData("shouldShowDialogs")) {
            return;
        }

        this.showDialogs(this.model.popAll());
    },

    /**
     * On leaving idle state.
     *
     * @private
     * @returns {void}
     */
    onLeavingIdleState: function() {
        this.model.storeData("isIdle", false);
    },

    /**
     * On entering showing custom messages state.
     *
     * @private
     * @returns {void}
     */
    onEnteringShowingCustomMessagesState: function() {
        this.showImmediateMessages();
    },

    /**
     * On entering delay before showing messages state.
     *
     * @private
     * @returns {void}
     */
    onEnteringDelayBeforeShowingMessagesState: function() {
        var me = this;

        clearTimeout(this.delayTimeout);

        this.startListeningToUserInput();
        this.delayTimeout = setTimeout(function() {
            me.fireEvent("notify:customMessages.delayComplete");
        }, this.DIALOG_DELAY);
    },

    /**
     * On leaving delay before showing messages state.
     *
     * @returns {void}
     */
    onLeavingDelayBeforeShowingMessagesState: function() {
        this.stopListeningToUserInput();
    },

    /**
     * On user input end.
     *
     * @param {Object} windowCoordinates The window coordinates.
     * @returns {void}
     */
    onUserInputEnd: function(windowCoordinates) {
        if (Sys.UserInputUtils.isCoordinateTarget(Game.stage.getGameContainer(), windowCoordinates)) {
            clearTimeout(this.delayTimeout);
            this.fireEvent("notify:customMessages.delayComplete");
        }
    },

    /**
     * Show the immediate messages if there are any.
     *
     * @private
     * @returns {void}
     */
    showImmediateMessages: function() {
        if (!this.model.readData("shouldShowDialogs")) {
            return;
        }

        this.showDialogs(this.model.popAllImmediate());
    },

    /**
     * Handle when a dialog is closed.
     *
     * @private
     * @param {string} id The id of the closed dialog.
     * @returns {void}
     */
    onDialogShown: function(id) {
        // Don't handle dialogs without an id (we always specify one in this module)
        if (!Sys.isDefined(id)) {
            return;
        }

        this.model.removeDialogToWaitFor(id);

        if (!this.model.hasDialogsToWaitFor()) {
            this.fireEvent("notify:customMessages.messagesClosed");
        }
    },

    /**
     * Tell the dialog manager to show the provided dialogs.
     *
     * @private
     * @param {Object[]} configs The dialog configurations.
     * @returns {void}
     */
    showDialogs: function(configs) {
        var me = this;

        if (configs.length > 0) {
            configs.forEach(function(config) {
                me.model.addDialogToWaitFor(config.id);

                // Stop auto play on customMessages if it is active
                if (Services.settingsManager.getSetting("autoPlayNrSpins") > 0 && (me.isErrorMessage(config.messageCode) || config.stopAutoplay)) {
                    me.fireEvent("request:autoPlayer.stop");
                }

                Services.dialog.show(config);

                // Notify that a fatal dialog has been triggered preventing further game play
                if (config.messageCode === me.ERRORS.DISPLAY_IMMEDIATELY_CLOSE || config.messageCode === me.ERRORS.DISPLAY_ON_IDLE_CLOSE) {
                    me.fireEvent("notify:customMessages.fatalMessageShowing", config.messageCode, config.text);
                }
            });
        }
        else {
            this.fireEvent("notify:customMessages.messagesClosed");
        }
    },

    /**
     * Function that tells if messageCode is one of custom messages.
     *
     * @param {string} messageCode Dialog messageCode.
     * @returns {boolean} Returns true if the messageCode is one of custom messages, false otherwise.
     */
    isErrorMessage: function(messageCode) {
        var me = this,
            errorKey;

        for (errorKey in me.ERRORS) {
            if (me.ERRORS.hasOwnProperty(errorKey) && messageCode === me.ERRORS[errorKey]) {
                return true;
            }
        }

        return false;
    },

    /**
     * Creates a dialog config from a message.
     *
     * @private
     * @static
     * @param {Object} message The message.
     * @returns {Object} The dialog config.
     */
    createConfigFromMessage: function(message) {
        var config = {
                title: Services.languageManager.getText(Language.Keys.messageCaption),
                text: message.message,
                buttons: [],
                immediate: true,
                id: message.code,
                messageCode: message.code
            },
            button;

        switch (message.code) {
            case this.ERRORS.DISPLAY_IMMEDIATELY_CLOSE:
                button = this.model.getCloseButtonConfig(this);
                config.hideGame = true;
                break;
            case this.ERRORS.DISPLAY_IMMEDIATELY_CONTINUE:
                button = this.model.getContinueButtonConfig(this, true);
                break;
            case this.ERRORS.DISPLAY_ON_IDLE_CLOSE:
                button = this.model.getCloseButtonConfig(this);
                config.hideGame = true;
                config.immediate = false;
                break;
            case this.ERRORS.DISPLAY_ON_IDLE_CONTINUE:
                button = this.model.getContinueButtonConfig(this);
                config.immediate = false;
                break;
            default:
                throw new Error("Technical Error");
        }

        if (Sys.isDefined(button)) {
            config.buttons.push(button);
        }

        return config;
    },

    /**
     * Prepares a dialog config for display.
     *
     * @private
     * @static
     * @param {Object} dialogConfig The dialog config.
     * @returns {Object} The prepared dialog config.
     */
    prepareDialogConfig: function(dialogConfig) {
        dialogConfig.text = this.scrubMessageString(dialogConfig.text);

        return dialogConfig;
    },

    /**
     * Scrubs a message string.
     *
     * Removes HTML tags and cuts it if it exceeds the max length.
     *
     * @private
     * @static
     * @param {string} message The message string.
     * @returns {string} Message string.
     */
    scrubMessageString: function(message) {
        var containsHTML = (message.match(/<\/?\w+((\s+\w+(\s*=\s*(?:\".*?\"|'.*?'|[^'\">\s]+))?)+\s*|\s*)\/?>/g) || []).length > 0,
            node = document.createElement("div");

        // Throw an error if the message contains illegal characters
        if (containsHTML) {
            throw new Error("Technical Error");
        }

        try {
            node.textContent = message;
        }
        catch (e) {
            throw new Error("Technical Error");
        }

        return message;
    },

    /**
     * Show a technical error.
     *
     * @private
     * @returns {void}
     */
    showTechnicalError: function() {
        this.model.storeData("showingError", true);

        if (this.model.readData("showingLoader")) {
            this.fireEvent("request:loadProgressHandler.showTechnicalError");
        }
        else {
            this.fireEvent("request:errorManager.handleError");
        }
    },

    /**
     * Reverts the spin.
     *
     * @returns {void}
     */
    revertSpin: function() {
        Services.activity.removeActivity("spin");

        this.fireEvent("request:moneyManager.revertBet");
        this.fireEvent("request:spin.cancelSpin");
    }
};

Core.CustomMessagesController = Sys.extend(Core.Controller, Core.CustomMessagesController, "Core.CustomMessagesController");
